import { services } from '@config/services';
import type {
  PagedResponse,
  Plugin,
  PluginBasicInfo,
  PluginBasicSettingsPayload,
  PluginCategory,
  PluginVersion,
  PluginVersionsResponse,
  PluginsListResponse,
} from '../types';
import { integrationApi } from './_base';

/**
 * Plugin-record endpoints (`/v1/plugin*`). All admin-facing CRUD for
 * plugins lives here. The merchant-scoped `merchantPlugin` endpoints are
 * in `./merchantPlugins.ts`. Execution logs (transformation service)
 * live in `./executionLogs.ts`.
 */

/**
 * Filters for the marketplace plugin list. Mirrors portalnew's
 * `integrationServices.getIntegrations` query-string shape so the
 * backend behaviour is identical.
 */
export interface PluginListArgs {
  searchString?: string;
  /** Which fields `searchString` is matched against server-side, e.g.
   * `['name', 'integrator']`. Omit to preserve the backend's name-only default. */
  searchFields?: string[];
  category?: PluginCategory;
  page?: number;
  pageSize?: number;
  sortBy?: string;
  sortAsc?: boolean;
  isPublished?: boolean;
  isPrivate?: boolean;
  manageableOnly?: boolean;
  /** Server-side merchant scope. portalnew sets this via the axios
   * interceptor (`injectEntityOverride: true`) when the active
   * entityOverride is a merchant. Portal2 callers pass it explicitly
   * — read from `useEntityOverride().scopedMerchantId`. Mirrors the
   * live URL: `&merchantId=<uuid>`. */
  merchantId?: string;
  /** Same as merchantId for vendor-scoped pages. */
  vendorId?: string;
}

/**
 * Build the query string for `GET /v1/plugin`. Param names + order
 * mirror portalnew's `integrationServices.getIntegrations` 1:1 so the
 * wire shape (and any backend-side params parsing) is identical:
 *
 *   ?searchBy=&page=&pageSize=&sortBy=&sortAsc=&type=&isPublished=&isPrivate=&manageableOnly=
 *
 * Verified against live: as merchant viewing Sales Channel 1, the URL is
 *   /integration/v1/plugin?&page=1&pageSize=50&sortBy=name&sortAsc=true&isPublished=true&isPrivate=false&merchantId=…
 * Tenant scope comes from the explicit `merchantId=` URL param that
 * `buildPluginQuery` appends below, driven by the active entity override.
 * It is the only tenant input here: do NOT remove it or tenant scoping
 * silently drops.
 *
 * `searchFields` has no portalnew equivalent — it's a new backend param
 * that tells the server which fields `searchBy` should match against
 * (e.g. `name,integrator`). Omitted entirely when not requested, so the
 * backend's name-only default is unaffected for other callers.
 */
function buildPluginQuery(args: PluginListArgs): string {
  const p = new URLSearchParams();
  if (args.searchString) p.set('searchBy', args.searchString);
  if (args.searchFields?.length) p.set('searchFields', args.searchFields.join(','));
  if (args.page != null) p.set('page', String(args.page));
  if (args.pageSize != null) p.set('pageSize', String(args.pageSize));
  if (args.sortBy) p.set('sortBy', args.sortBy);
  if (args.sortAsc != null) p.set('sortAsc', String(args.sortAsc));
  if (args.category) p.set('type', args.category);
  if (args.isPublished != null) p.set('isPublished', String(args.isPublished));
  if (args.isPrivate != null) p.set('isPrivate', String(args.isPrivate));
  if (args.manageableOnly != null) p.set('manageableOnly', String(args.manageableOnly));
  if (args.merchantId) p.set('merchantId', args.merchantId);
  if (args.vendorId) p.set('vendorId', args.vendorId);
  return p.toString();
}

/**
 * Phase 5 mutation request descriptors, exported separately so the
 * wire-shape lock tests can assert the URL/method/body without going
 * through the network layer. `query: phase5RequestBuilders.X` keeps the
 * api wiring identical to defining the function inline.
 */
export const phase5RequestBuilders = {
  createPlugin: (body: PluginBasicSettingsPayload): { url: string; method: 'POST'; body: PluginBasicSettingsPayload } => ({
    url: `${services.integration}/v1/plugin`,
    method: 'POST',
    body,
  }),
  updatePluginBasicSettings: ({ pluginId, body }: { pluginId: string; body: PluginBasicSettingsPayload }): { url: string; method: 'PATCH'; body: PluginBasicSettingsPayload } => ({
    url: `${services.integration}/v1/plugin/${encodeURIComponent(pluginId)}`,
    method: 'PATCH',
    body,
  }),
  deletePlugin: (pluginId: string): { url: string; method: 'DELETE' } => ({
    url: `${services.integration}/v1/plugin/${encodeURIComponent(pluginId)}`,
    method: 'DELETE',
  }),
};

/**
 * Contact Vendor inquiry body. Portal side of the "Contact the Vendor"
 * feature. Submitting POSTs business name / contact email / message to
 * integration-service, which emails the vendor.
 */
export interface VendorInquiryBody {
  businessName: string;
  contactEmail: string;
  message: string;
  /**
   * Server-side entity scope. portalnew set this via the axios interceptor
   * (`injectEntityOverride: true`), which on a POST writes a scalar
   * `merchantId` (acting-as merchant) or `vendorId` (acting-as vendor) into
   * the BODY — and nothing for admin scope. Portal2 callers pass it
   * explicitly from `useEntityOverride().scopedMerchantId / scopedVendorId`.
   * Exactly one is set, and only when a concrete entity is scoped.
   */
  merchantId?: string;
  vendorId?: string;
}

export interface SendVendorInquiryArgs {
  pluginId: string;
  body: VendorInquiryBody;
}

/**
 * Contact Vendor request descriptor, exported separately so the wire-shape
 * lock test asserts the URL/method/body without the network layer (mirrors
 * `phase5RequestBuilders`). Live wire (reconstructed from portalnew
 * `integrationServices.submitVendorInquiry` + the injectEntityOverride
 * interceptor):
 *
 *   POST /integration/v1/plugins/{pluginId}/inquiries
 *   body { businessName, contactEmail, message, merchantId? | vendorId? }
 *
 * Note the plural `plugins` segment: portalnew uses singular `/v1/plugin/*`
 * for plugin records but `/v1/plugins/{id}/inquiries` for the inquiry
 * sub-resource. Matched verbatim.
 */
export const contactVendorRequestBuilders = {
  sendVendorInquiry: ({ pluginId, body }: SendVendorInquiryArgs): { url: string; method: 'POST'; body: VendorInquiryBody } => ({
    url: `${services.integration}/v1/plugins/${encodeURIComponent(pluginId)}/inquiries`,
    method: 'POST',
    body,
  }),
};

const pluginsApi = integrationApi.injectEndpoints({
  endpoints: (builder) => ({
    /**
     * Marketplace plugin list — `GET /v1/plugin`. Mirrors portalnew's
     * `getIntegrations` 1:1. Returns the FULL response wrapper (not just
     * the array) because the sidebar categories come from
     * `allowedPluginTypes` on this same response — there is no separate
     * `/v1/pluginType` lookup for that purpose.
     */
    getPlugins: builder.query<PluginsListResponse, PluginListArgs | void>({
      query: (args) => ({
        url: `${services.integration}/v1/plugin?${buildPluginQuery(args ?? {})}`,
        method: 'GET',
      }),
      transformResponse: (
        response: PluginsListResponse | Plugin[] | PagedResponse<Plugin>,
      ): PluginsListResponse => {
        if (Array.isArray(response)) {
          return { plugins: response, total: response.length };
        }
        const wrapped = response as PluginsListResponse & PagedResponse<Plugin>;
        return {
          plugins: wrapped.plugins ?? wrapped.data ?? [],
          total: wrapped.total ?? 0,
          allowedPluginTypes: wrapped.allowedPluginTypes,
        };
      },
      providesTags: (result) =>
        result?.plugins
          ? [
              ...result.plugins.map((p) => ({ type: 'Plugins' as const, id: p.id })),
              { type: 'Plugins' as const, id: 'LIST' },
            ]
          : [{ type: 'Plugins' as const, id: 'LIST' }],
    }),
    /**
     * Plugin-level basic info (type, webhook mode, etc.). The integration
     * service wraps the row in a `{ pluginData: {...} }` envelope — we
     * unwrap here so callers can read `.type` directly. Matches portalnew's
     * reducer (`basicInfo: data.payload.pluginData`).
     */
    getPluginBasicInfo: builder.query<PluginBasicInfo, string>({
      query: (pluginId) => ({
        url: `${services.integration}/v1/plugin/${encodeURIComponent(pluginId)}`,
        method: 'GET',
      }),
      transformResponse: (response: PluginBasicInfo | { pluginData?: PluginBasicInfo }): PluginBasicInfo => {
        const wrapped = response as { pluginData?: PluginBasicInfo };
        return wrapped.pluginData ?? (response as PluginBasicInfo);
      },
      providesTags: (_r, _e, pluginId) => [{ type: 'PluginBasicInfo', id: pluginId }],
    }),
    /**
     * Live shape (verified 2026-05-28 against
     *   GET /integration/v1/plugin/{pluginId}/version/{pluginVersionId}):
     *
     *   { integratorId, pluginName, pluginVersion: {
     *       variables, readOnlyVariableKeys, events, id, name, isDraft,
     *   } }
     *
     * This is the canonical source for the plugin's DECLARED variables
     * + read-only key list — neither is on the merchantPlugin row.
     * ConnectionDetails fetches this in parallel with the merchantPlugin
     * detail and merges `variables` + `readOnlyVariableKeys` into the
     * `MerchantPluginDetail` so ConnectVariables can render the
     * editable form. Without this call, every override key falls into
     * the "system-generated → read-only" branch and the form renders as
     * a list of copy-only display rows — that's how the user first
     * reported "edit page doesn't show any variables".
     */
    getPluginVersionDetail: builder.query<
      { variables?: Record<string, string>; readOnlyVariableKeys?: string[]; integratorId?: string; pluginName?: string },
      { pluginId: string; pluginVersionId: string }
    >({
      query: ({ pluginId, pluginVersionId }) => ({
        url: `${services.integration}/v1/plugin/${encodeURIComponent(pluginId)}/version/${encodeURIComponent(pluginVersionId)}`,
        method: 'GET',
      }),
      transformResponse: (response: {
        integratorId?: string;
        pluginName?: string;
        pluginVersion?: { variables?: Record<string, string>; readOnlyVariableKeys?: string[] };
      }) => ({
        integratorId: response?.integratorId,
        pluginName: response?.pluginName,
        variables: response?.pluginVersion?.variables ?? {},
        readOnlyVariableKeys: response?.pluginVersion?.readOnlyVariableKeys ?? [],
      }),
      providesTags: (_r, _e, { pluginId, pluginVersionId }) => [
        { type: 'PluginVersions', id: `${pluginId}:${pluginVersionId}` },
      ],
    }),
    /**
     * Live shape (verified 2026-05-28 on /admin/plugins/{id}/about):
     *   GET /integration/v1/plugin/{pluginId}/version
     *   Response: { pluginVersions: [{ id, name, isDraft, ... }] }
     *
     * Used by the Learn More page to compute the latest non-draft version
     * client-side. PluginDto itself doesn't carry a `version` field today —
     * we sort the returned list by name DESC and pick the first non-draft
     * (the documented client-side tiebreak; PluginVersionBasicDto doesn't
     * expose CreatedAt on the wire). Failure here is non-fatal; the page
     * just renders "No information provided" for version (vendor users
     * 403 on this endpoint; the rest of the page still renders).
     */
    getPluginVersions: builder.query<PluginVersion[], string>({
      query: (pluginId) => ({
        url: `${services.integration}/v1/plugin/${encodeURIComponent(pluginId)}/version`,
        method: 'GET',
      }),
      transformResponse: (response: PluginVersionsResponse | PluginVersion[]): PluginVersion[] => {
        if (Array.isArray(response)) return response;
        return response?.pluginVersions ?? [];
      },
      providesTags: (_r, _e, pluginId) => [
        { type: 'PluginVersions', id: pluginId },
      ],
    }),
    /**
     * Phase 5 — IntegrationsManager admin actions.
     *
     * Create a new plugin record. Live wire (verified 2026-05-28):
     *   POST /integration/v1/plugin
     *   Body: PluginBasicSettingsPayload
     *   Returns: { plugin: { id, ... } }  (caller navigates to /admin/plugins/{id}/0)
     *
     * Logo upload is a separate caller-side dance (createFile presign →
     * blob PUT → strip SAS); the resulting public URL is passed in via
     * `body.logoUrl`. Saving "" preserves the no-logo state.
     */
    createPlugin: builder.mutation<{ plugin: { id: string; [key: string]: unknown } }, PluginBasicSettingsPayload>({
      query: phase5RequestBuilders.createPlugin,
      invalidatesTags: [
        { type: 'Plugins', id: 'LIST' },
      ],
    }),
    /**
     * Update an existing plugin's basic settings. Live wire (verified
     * 2026-05-28): PATCH /integration/v1/plugin/{pluginId}, same body
     * shape as create. Returns the updated `{ plugin: {...} }` envelope.
     * Invalidates both the list and the basic-info cache so the row
     * re-renders with fresh data after save.
     */
    updatePluginBasicSettings: builder.mutation<
      { plugin: { id: string; [key: string]: unknown } },
      { pluginId: string; body: PluginBasicSettingsPayload }
    >({
      query: phase5RequestBuilders.updatePluginBasicSettings,
      invalidatesTags: (_r, _e, { pluginId }) => [
        { type: 'PluginBasicInfo', id: pluginId },
        { type: 'Plugins', id: pluginId },
        { type: 'Plugins', id: 'LIST' },
      ],
    }),
    /**
     * Delete a plugin. Live wire (verified 2026-05-28):
     *   DELETE /integration/v1/plugin/{pluginId}
     *   No body. portalnew refetches the list with `?manageableOnly=true`
     *   on success — we just invalidate the Plugins LIST tag and let RTK
     *   Query refetch whatever subscriber currently holds the list.
     */
    deletePlugin: builder.mutation<void, string>({
      query: phase5RequestBuilders.deletePlugin,
      invalidatesTags: (_r, _e, pluginId) => [
        { type: 'PluginBasicInfo', id: pluginId },
        { type: 'Plugins', id: pluginId },
        { type: 'Plugins', id: 'LIST' },
      ],
    }),
    /**
     * Contact Vendor — POST an inquiry to the vendor behind a vendor plugin.
     * Live wire (reconstructed from portalnew):
     *   POST /integration/v1/plugins/{pluginId}/inquiries
     *   body { businessName, contactEmail, message, merchantId? | vendorId? }
     *
     * No `invalidatesTags`: an inquiry emails the vendor and mutates no
     * cached entity (matches portalnew's `takeLatest` saga, which dispatched
     * no refetch). No `transformErrorResponse`: callers read the raw
     * `{ status, data }` so the modal can switch copy on
     * `data.errorCode === 'NO_VENDOR_CONTACT_INFO'`.
     */
    sendVendorInquiry: builder.mutation<unknown, SendVendorInquiryArgs>({
      query: contactVendorRequestBuilders.sendVendorInquiry,
    }),
  }),
});

export const {
  useGetPluginsQuery,
  useGetPluginBasicInfoQuery,
  useGetPluginVersionsQuery,
  useGetPluginVersionDetailQuery,
  useCreatePluginMutation,
  useUpdatePluginBasicSettingsMutation,
  useDeletePluginMutation,
  useSendVendorInquiryMutation,
} = pluginsApi;
